import { put, all, select } from "redux-saga/effects";
import _ from "lodash";

import makeRequest from "rdx/utils/makeRequest";
import getErrorActions from "rdx/utils/getErrorActions";
import actions from "rdx/actions";
import selectors from "rdx/selectors";
import { orgProjectsApi } from "rdx/modules/enterpriseProjects/endpoints";
import { ORG_PROJECTS_TAGS } from "rdx/modules/enterpriseProjects/tags";
import { CrmActivityFormDataT } from "types/Project/crmActivity";
import { keysToSnakeCase, camelizeKeys, formatCrmActivityForLog, formatPreSalesCrmActivity } from "lib/helpers";
import messages from "../messages";

type CrmActivityParamsT = {
  payload: CrmActivityFormDataT;
};

const successMessage = (activity) => {
  switch (activity.kind) {
    case "reminder":
      return messages.CRM_REMINDER_SCHEDULED;
    case "email":
      return messages.CRM_EMAIL_SENT;
    case "meeting":
      return messages.CRM_MEETING_CREATE_SUCCESS;
    default:
      return messages.CRM_ACTIVITY_CREATE_SUCCESS;
  }
};

function* createNewCrmActivity(action: CrmActivityParamsT) {
  const params = keysToSnakeCase(action.payload);

  const { success, data, error } = yield* makeRequest.post("/sales_enablement_activities", params);

  if (success && data) {
    const activity = camelizeKeys(data.body);
    const projectDetail = yield select(selectors.getProjectDetails);

    const projectList = yield select(selectors.selectProviderLeadsList);
    const idxOfListProject = projectList.providerLeads.findIndex(
      (project) => project.homeowner.id === action.payload.leadId,
    );

    const logActivity = formatCrmActivityForLog(activity);

    yield all([
      put(
        actions.newSuccessEvent({
          message: successMessage(activity),
          payload: { ...data.body },
        }),
      ),
      put(actions.setAlertMessageVisible({ message: successMessage(activity), severity: "success" })),
      ...(idxOfListProject >= 0 ? [put(actions.addCrmActivityToList({ projectIdx: idxOfListProject, activity }))] : []),
      ...(activity.kind !== "email"
        ? [put(actions.updateCrmActivityInPreSalesList(formatPreSalesCrmActivity(activity)))]
        : []),
      ...(action.payload.leadId === projectDetail?.lead?.id
        ? [put(actions.addCrmActivityToDetail(logActivity)), put(actions.addProjectCrmActivity(activity))]
        : []),
      yield put(
        orgProjectsApi.util.invalidateTags([
          ORG_PROJECTS_TAGS.ORG_PRE_SALES_PROJECTS,
          ORG_PROJECTS_TAGS.ORG_POST_SALES_PROJECTS,
          ORG_PROJECTS_TAGS.ORG_COMPLETED_PROJECTS,
        ]),
      ),
    ]);
  } else {
    return getErrorActions({
      error,
      message: messages.ERROR_CREATING_CRM_ACTIVITY,
    });
  }

  return null;
}

export default createNewCrmActivity;
